import React from "react";
import { motion } from "framer-motion";
import { Phone, CameraOff, MapPin, Gift } from "lucide-react";
import { CountdownTimer } from "./CountdownTimer";
import { RSVPForm } from "./RSVPForm";
import { FallingOverlay } from "./CherryBlossom";
import { Monogram } from "./Monogram";

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.25 }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

const Divider = ({ colors }) => (
  <div className="flex items-center justify-center gap-3 my-2">
    <span style={{ width: 60, height: 1, background: `${colors.primary}55` }} />
    <span style={{ width: 7, height: 7, transform: "rotate(45deg)", background: colors.accent }} />
    <span style={{ width: 60, height: 1, background: `${colors.primary}55` }} />
  </div>
);

const CustomBlocks = ({ blocks, position, colors, fs, fw }) => {
  const items = (blocks || []).filter((b) => (b.position || "beforeRsvp") === position);
  if (!items.length) return null;
  return (
    <section className="px-6 sm:px-8 py-10">
      <div className="mx-auto max-w-xl flex flex-col gap-10">
        {items.map((b) => (
          <Reveal key={b.id}>
            <div className={b.align === "left" ? "text-left" : "text-center"} style={{ marginTop: b.offsetY || 0 }}>
              {b.type === "image" ? (
                <>
                  {b.imageUrl && (
                    <img src={b.imageUrl} alt={b.heading || "photo"} className="w-full rounded-2xl shadow-md object-cover" style={{ maxHeight: 460 }} />
                  )}
                  {b.heading && (
                    <p className="font-display italic mt-3" dir="auto" style={{ fontSize: fs(20, "body"), fontWeight: fw("body"), color: colors.text }}>{b.heading}</p>
                  )}
                </>
              ) : (
                <>
                  {b.heading && (
                    <p className={b.arabic ? "font-arabic leading-loose" : "font-display tracking-tight"} dir="auto" style={{ fontSize: fs(b.arabic ? "clamp(1.6rem,7vw,2.4rem)" : "clamp(1.8rem,7vw,2.6rem)", "headings"), fontWeight: fw("headings"), color: colors.primary }}>{b.heading}</p>
                  )}
                  {b.body && (
                    <p className={b.arabic ? "font-arabic leading-loose mt-3" : "font-body mt-3"} dir="auto" style={{ fontSize: fs(b.arabic ? "1.4rem" : "0.95rem", "body"), fontWeight: fw("body"), color: colors.text, lineHeight: 1.9 }}>{b.body}</p>
                  )}
                </>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Corners = ({ show, data }) => {
  if (!show) return null;
  const size = data.cornerSize ?? 32;
  const opacity = (data.cornerOpacity ?? 90) / 100;
  const inset = data.cornerInset ?? 0; // px; negative pushes florals outward into the corners
  const base = { position: "absolute", width: `${size}%`, maxWidth: 240, pointerEvents: "none", zIndex: 1, opacity };
  return (
    <>
      <img src="/corner_floral.png" alt="" style={{ ...base, top: inset, left: inset }} />
      <img src="/corner_floral.png" alt="" style={{ ...base, top: inset, right: inset, transform: "scaleX(-1)" }} />
      <img src="/corner_floral.png" alt="" style={{ ...base, bottom: inset, left: inset, transform: "scaleY(-1)" }} />
      <img src="/corner_floral.png" alt="" style={{ ...base, bottom: inset, right: inset, transform: "scale(-1,-1)" }} />
    </>
  );
};

export const InvitationView = ({ data, API, invitee, eventKey = "main" }) => {
  const c = data.colors;
  const scale = data.fontScale || 1;
  const sz = data.sizes || {};
  const wt = data.weights || {};
  // fs(base, group): base can be a number (px) or a CSS string (clamp/rem)
  const fs = (base, group) => {
    const m = scale * (sz[group] || 1);
    return typeof base === "number" ? base * m : `calc(${m} * (${base}))`;
  };
  // fw(group): font-weight per text group (defaults to 400)
  const fw = (group) => wt[group] || 400;
  const bgAlpha = Math.round(Math.min(Math.max(data.bgLighten ?? 60, 0), 100) / 100 * 255).toString(16).padStart(2, "0");
  const cf = data.cornerFrames;
  const rootBg = data.bgImagePage
    ? `linear-gradient(${c.bg}${bgAlpha}, ${c.bg}${bgAlpha}), url(${data.bgImagePage}) center/cover no-repeat`
    : c.bg;

  return (
    <div className="relative w-full" style={{ background: rootBg, color: c.text }} data-testid="invitation-view">
      {/* Bismillah intro / intro video */}
      {data.introVideo ? (
        <section className="relative min-h-screen w-full overflow-hidden bg-black" data-testid="intro-video">
          <video src={data.introVideo} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
        </section>
      ) : (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${data.bgImageIntro})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: `${c.bg}${bgAlpha}` }} />
        <motion.div className="relative z-10 text-center px-6 sm:px-8" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: "easeOut" }}>
          <p dir="auto" className="font-arabic leading-relaxed" style={{ fontSize: fs("clamp(2.2rem,9vw,3.6rem)", "arabic"), fontWeight: fw("arabic"), color: c.primary }}>بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</p>
          {data.bismillahSubtitle && (<p dir="auto" className="font-body tracking-luxe mt-6" style={{ fontSize: fs(12, "labels"), fontWeight: fw("labels"), color: c.secondary }}>{data.bismillahSubtitle}</p>)}
        </motion.div>
      </section>
      )}

      <CustomBlocks blocks={data.customBlocks} position="afterIntro" colors={c} fs={fs} fw={fw} />

      {invitee && invitee.guestName && (
        <section className="px-6 sm:px-8 pt-8 pb-2 text-center" data-testid="invitee-banner">
          <Reveal>
            <div className="mx-auto max-w-md rounded-2xl px-6 py-5" style={{ background: "rgba(255,255,255,0.5)", border: `1px solid ${c.accent}66` }}>
              <p className="font-body tracking-luxe" style={{ fontSize: fs(11, "labels"), fontWeight: fw("labels"), color: c.secondary }}>{(data.inviteLabel || "Specially invited").toUpperCase()}</p>
              <p dir="auto" className="font-display italic mt-2" style={{ fontSize: fs(30, "headings"), fontWeight: fw("headings"), color: c.primary, lineHeight: 1.2 }}>{invitee.guestName}</p>
              {invitee.note && <p dir="auto" className="font-body mt-2" style={{ fontSize: fs(13, "labels"), color: c.text }}>{invitee.note}</p>}
            </div>
          </Reveal>
        </section>
      )}

      {/* Greeting */}
      <section className="relative overflow-hidden py-24 px-6 sm:px-8 text-center">
        <Corners show={cf} data={data} />
        <div className="mx-auto max-w-xl">
          <Reveal>
            <p dir="auto" className="font-arabic leading-loose" style={{ fontSize: fs("clamp(1.6rem,6vw,2.4rem)", "arabic"), fontWeight: fw("arabic"), color: c.primary }}>{data.greetingArabic}</p>
            {data.greetingSubtitle && (<p dir="auto" className="font-body tracking-luxe mt-4" style={{ fontSize: fs(12, "labels"), fontWeight: fw("labels"), color: c.secondary }}>{data.greetingSubtitle}</p>)}
          </Reveal>
          <Reveal delay={0.1}>
            <p dir="auto" className="font-body tracking-luxe mt-10" style={{ fontSize: fs(12, "labels"), fontWeight: fw("labels"), color: c.secondary }}>{data.blessingLine}</p>
            <p dir="auto" className="font-arabic mt-3" style={{ fontSize: fs("clamp(1.4rem,5vw,2rem)", "arabic"), fontWeight: fw("arabic"), color: c.primary }}>{data.blessingArabic}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <p dir="auto" className="font-display italic mt-10" style={{ fontSize: fs(20, "body"), fontWeight: fw("body"), color: c.text }}>{data.hostText}</p>
          </Reveal>
        </div>
      </section>

      <CustomBlocks blocks={data.customBlocks} position="top" colors={c} fs={fs} fw={fw} />

      {/* Couple */}
      <section className="relative py-20 px-6 sm:px-8 overflow-hidden" style={data.bgImageCouple ? { backgroundImage: `linear-gradient(${c.bg}cc, ${c.bg}cc), url(${data.bgImageCouple})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined} data-testid="couple-section">
        {data.cherryBlossom && <FallingOverlay variant={data.overlayType || "petals"} count={{ light: 10, medium: 18, heavy: 34 }[data.petalDensity] || 18} />}
        {(data.coupleDecor || "sides") === "corners" && <Corners show={cf} data={data} />}
        {(data.coupleDecor || "sides") === "sides" && (
          <>
            <img src={data.floralAccent} alt="floral" className="pointer-events-none absolute -top-6 -left-8 w-32 sm:w-40 opacity-90 select-none" style={{ transform: "rotate(-8deg)" }} />
            <img src={data.floralAccent} alt="floral" className="pointer-events-none absolute -bottom-8 -right-8 w-36 sm:w-44 opacity-90 select-none" style={{ transform: "rotate(170deg)" }} />
          </>
        )}
        <div className="relative z-10 max-w-lg mx-auto text-center flex flex-col gap-8">
          <Reveal>
            <div>
              <h1 dir="auto" className="font-names tracking-tight" style={{ fontSize: fs("clamp(2.6rem,11vw,4rem)", "names"), fontWeight: fw("names"), color: c.text, lineHeight: 1 }}>{data.groomName}</h1>
              <p dir="auto" className="font-display tracking-[0.2em] mt-1" style={{ fontSize: fs(22, "lastNames"), fontWeight: fw("lastNames"), color: c.text }}>{data.groomLastName?.toUpperCase()}</p>
              <p dir="auto" className="font-body mt-3" style={{ fontSize: fs(14, "parents"), fontWeight: fw("parents"), color: c.secondary }}>{data.groomParents}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="font-names italic block" style={{ fontSize: fs(44, "connector"), fontWeight: fw("connector"), color: c.secondary, lineHeight: 1 }}>{data.coupleConnector || "with"}</span>
          </Reveal>
          <Reveal delay={0.15}>
            <div>
              <h1 dir="auto" className="font-names tracking-tight" style={{ fontSize: fs("clamp(2.6rem,11vw,4rem)", "names"), fontWeight: fw("names"), color: c.text, lineHeight: 1 }}>{data.brideName}</h1>
              <p dir="auto" className="font-display tracking-[0.2em] mt-1" style={{ fontSize: fs(22, "lastNames"), fontWeight: fw("lastNames"), color: c.text }}>{data.brideLastName?.toUpperCase()}</p>
              <p dir="auto" className="font-body mt-3" style={{ fontSize: fs(14, "parents"), fontWeight: fw("parents"), color: c.secondary }}>{data.brideParents}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {data.couplePhoto && (
        <section className="px-6 sm:px-8 pb-6" data-testid="couple-photo">
          <div className="mx-auto max-w-sm">
            <img src={data.couplePhoto} alt="couple" className="w-full rounded-2xl shadow-md object-cover" style={{ maxHeight: 480 }} />
          </div>
        </section>
      )}

      <CustomBlocks blocks={data.customBlocks} position="afterCouple" colors={c} fs={fs} fw={fw} />

      {/* Event details */}
      <section className="relative overflow-hidden py-20 px-6 sm:px-8 text-center">
        <Corners show={cf} data={data} />
        <div className="mx-auto max-w-xl">
          <Reveal>
            <p dir="auto" className="font-display tracking-[0.15em]" style={{ fontSize: fs(30, "headings"), fontWeight: fw("headings"), color: c.primary }}>{data.eventName?.toUpperCase()}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p dir="auto" className="font-display mt-6" style={{ fontSize: fs(30, "headings"), fontWeight: fw("headings"), color: c.text }}>{data.dateGregorian}</p>
            <p dir="auto" className="font-display mt-1" style={{ fontSize: fs(26, "headings"), fontWeight: fw("headings"), color: c.primary }}>{data.dateHijri}</p>
            <p dir="auto" className="font-body tracking-luxe mt-4" style={{ fontSize: fs(12, "labels"), fontWeight: fw("labels"), color: c.secondary }}>{data.dayName?.toUpperCase()}</p>
            {data.eventSubtitle && (
              <p dir="auto" className="font-display italic mt-4" style={{ fontSize: fs(20, "body"), fontWeight: fw("body"), color: c.secondary, lineHeight: 1.6 }}>{data.eventSubtitle}</p>
            )}
          </Reveal>
          <div className="my-10"><Divider colors={c} /></div>
          <Reveal delay={0.15}>
            <p dir="auto" className="font-venue" style={{ fontSize: fs("clamp(2rem,8vw,3rem)", "venue"), fontWeight: fw("venue"), color: c.primary, lineHeight: 1.05 }}>{data.venueName}</p>
            <p dir="auto" className="font-body tracking-widest mt-4 uppercase" style={{ fontSize: fs(12, "labels"), fontWeight: fw("labels"), color: c.text }}>{data.venueAddress}</p>
            <p dir="auto" className="font-display mt-8" style={{ fontSize: fs(26, "headings"), fontWeight: fw("headings"), color: c.text }}>{data.timeText}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <p dir="auto" className="font-display tracking-[0.15em] mt-14 mb-8" style={{ fontSize: fs(26, "headings"), fontWeight: fw("headings"), color: c.primary }}>{data.countdownTitle}</p>
            <CountdownTimer target={data.eventDateTime} colors={c} scale={scale * (sz.countdown || 1)} />
          </Reveal>
          <CustomBlocks blocks={data.customBlocks} position="afterCountdown" colors={c} fs={fs} fw={fw} />
        </div>
      </section>

      <CustomBlocks blocks={data.customBlocks} position="afterEvent" colors={c} fs={fs} fw={fw} />

      {/* Dua / Blessing */}
      {data.duaEnabled && (
        <section className="py-16 px-6 sm:px-8 text-center">
          <div className="mx-auto max-w-xl">
            <Reveal>
              <Monogram data={data} size={fs(105, "monogram")} />
            </Reveal>
          </div>
          <div className="relative overflow-hidden -mx-6 sm:-mx-8 px-6 sm:px-8 mt-6 pt-16 pb-16">
            <Corners show={cf} data={data} />
            <div className="relative z-10 mx-auto max-w-xl">
            {data.duaIntro && (
              <Reveal delay={0.1}>
                <p dir="auto" className="font-display italic" style={{ fontSize: fs(20, "body"), fontWeight: fw("body"), color: c.text, lineHeight: 1.7 }}>{data.duaIntro}</p>
              </Reveal>
            )}
            <Reveal delay={0.15}>
              {data.duaArabic && (<p dir="auto" className="font-arabic leading-loose mt-8" style={{ fontSize: fs("clamp(1.5rem,6vw,2.2rem)", "arabic"), fontWeight: fw("arabic"), color: c.primary }}>{data.duaArabic}</p>)}
              {data.duaAmeen && (<p dir="auto" className="font-arabic mt-4" style={{ fontSize: fs("clamp(1.2rem,4.5vw,1.6rem)", "arabic"), fontWeight: fw("arabic"), color: c.primary }}>{data.duaAmeen}</p>)}
            </Reveal>
            {data.duaTranslation && (
              <Reveal delay={0.2}>
                <p dir="auto" className="font-display italic mt-6" style={{ fontSize: fs(18, "body"), fontWeight: fw("body"), color: c.secondary, lineHeight: 1.6 }}>{data.duaTranslation}</p>
              </Reveal>
            )}
            </div>
          </div>
        </section>
      )}

      {/* Contacts */}
      {(data.contacts || []).length > 0 && (
        <section className="py-16 px-6 sm:px-8 text-center">
          <Reveal>
            <p dir="auto" className="font-body tracking-luxe mb-8" style={{ fontSize: fs(12, "labels"), fontWeight: fw("labels"), color: c.secondary }}>{(data.contactsTitle || "").toUpperCase()}</p>
            <div className="mx-auto max-w-md grid gap-3" data-testid="contacts-list">
              {data.contacts.map((ct) => (
                <a key={ct.id} href={`tel:${(ct.phone || "").replace(/\s/g, "")}`} className="flex flex-col items-center gap-1 rounded-xl px-5 py-3" style={{ border: `1px solid ${c.primary}22`, background: "rgba(255,255,255,0.4)" }}>
                  <span dir="auto" className="font-display" style={{ fontSize: fs(20, "body"), fontWeight: fw("body"), color: c.text }}>{ct.name}</span>
                  <span className="flex items-center gap-2 font-body" style={{ fontSize: fs(14, "labels"), fontWeight: fw("labels"), color: c.primary }}><Phone size={15} /> {ct.phone}</span>
                </a>
              ))}
            </div>
          </Reveal>
        </section>
      )}

      <CustomBlocks blocks={data.customBlocks} position="afterContacts" colors={c} fs={fs} fw={fw} />
      <CustomBlocks blocks={data.customBlocks} position="beforeRsvp" colors={c} fs={fs} fw={fw} />

      {/* Camera notice */}
      {data.cameraEnabled && data.cameraText && (
        <section className="py-12 px-6 sm:px-8">
          <div className="mx-auto max-w-md">
            <Reveal>
              <div className="rounded-[2rem] p-8 text-center" style={{ background: "rgba(255,255,255,0.55)", border: `1px solid ${c.primary}22`, boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }} data-testid="camera-notice">
                <CameraOff className="mx-auto mb-5" size={34} style={{ color: c.secondary }} />
                <p dir="auto" className="font-display" style={{ fontSize: fs(20, "body"), fontWeight: fw("body"), color: c.text, lineHeight: 1.7 }}>{data.cameraText}</p>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* RSVP */}
      <section className="relative overflow-hidden py-20 px-6 sm:px-8 text-center">
        <div className="mx-auto max-w-xl">
          <Reveal>
            <p dir="auto" className="font-display tracking-[0.15em]" style={{ fontSize: fs(30, "headings"), fontWeight: fw("headings"), color: c.primary }}>{data.rsvpTitle}</p>
            <p dir="auto" className="font-body mt-3 mb-10" style={{ fontSize: fs(14, "labels"), fontWeight: fw("labels"), color: c.secondary }}>{data.rsvpSubtitle}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <RSVPForm colors={c} API={API} attendLabel={data.rsvpAttendLabel || "Attending"} declineLabel={data.rsvpDeclineLabel || "Not Attending"} invitee={invitee} eventKey={eventKey} />
          </Reveal>
        </div>
      </section>

      <CustomBlocks blocks={data.customBlocks} position="bottom" colors={c} fs={fs} fw={fw} />

      {/* Compliments */}
      {data.complimentsEnabled && (data.complimentsNames || []).length > 0 && (
        <section className="pt-10 pb-4 px-6 sm:px-8 text-center">
          <Reveal>
            <p dir="auto" className="font-display italic" style={{ fontSize: fs(26, "headings"), fontWeight: fw("headings"), color: c.primary }}>{data.complimentsTitle}</p>
            <div className="mt-4 flex flex-col gap-2" data-testid="compliments-list">
              {data.complimentsNames.map((n) => (
                <p key={n.id} dir="auto" className="font-display italic" style={{ fontSize: fs(20, "body"), fontWeight: fw("body"), color: c.text }}>{n.name}</p>
              ))}
            </div>
          </Reveal>
        </section>
      )}

      {/* Map */}
      {data.mapEnabled && data.mapQuery && (
        <section className="py-10 px-6 sm:px-8">
          <div className="mx-auto max-w-xl">
            <Reveal>
              <div className="rounded-2xl overflow-hidden shadow-md" style={{ border: `1px solid ${c.primary}22` }}>
                <iframe title="venue-map" src={`https://maps.google.com/maps?q=${encodeURIComponent(data.mapQuery)}&output=embed`} width="100%" height="300" style={{ border: 0, display: "block" }} loading="lazy" />
              </div>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.mapQuery)}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 font-body tracking-wide" style={{ fontSize: fs(14, "labels"), fontWeight: fw("labels"), color: c.primary }} data-testid="open-in-maps"><MapPin size={15} /> Open in Maps</a>
            </Reveal>
          </div>
        </section>
      )}

      {/* Gift note */}
      {data.giftEnabled && data.giftText && (
        <section className="py-12 px-6 sm:px-8">
          <div className="mx-auto max-w-md">
            <Reveal>
              <div className="relative rounded-[2rem] px-8 py-10 text-center overflow-hidden" style={{ background: `linear-gradient(150deg, ${c.primary} 0%, ${c.secondary} 100%)`, boxShadow: `0 18px 40px ${c.primary}44` }} data-testid="gift-note">
                <div className="absolute inset-[6px] rounded-[1.7rem] pointer-events-none" style={{ border: `1px solid ${c.accent}88` }} />
                <div className="relative z-10">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5" style={{ background: `${c.accent}22`, border: `1px solid ${c.accent}` }}>
                    <Gift size={28} style={{ color: c.accent }} />
                  </span>
                  <p dir="auto" className="font-display tracking-[0.12em]" style={{ fontSize: fs(26, "headings"), fontWeight: fw("headings"), color: c.accent }}>{data.giftTitle}</p>
                  <p dir="auto" className="font-display italic mt-4" style={{ fontSize: fs(19, "body"), fontWeight: fw("body"), color: "#fff", lineHeight: 1.75 }}>{data.giftText}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <footer className="relative overflow-hidden py-12 text-center">
        <Corners show={cf} data={data} />
        <p dir="auto" className="font-arabic" style={{ fontSize: fs(24, "arabic"), fontWeight: fw("arabic"), color: c.primary }}>{data.footerArabic}</p>
        <p dir="auto" className="font-body tracking-luxe text-[10px] mt-3" style={{ color: c.secondary }}>{data.footerName?.trim() ? data.footerName : `${data.groomName?.toUpperCase()} \u0026 ${data.brideName?.toUpperCase()}`}</p>
      </footer>
    </div>
  );
};
